import * as Sentry from '@sentry/react';
import { format, toZonedTime } from 'date-fns-tz';
import { config } from '../config/environment';
import { dummyServerStats } from '../data/dummyData';
import {
  getServerStatsShifts,
  getServerStatsServers,
  getServerStatsPremiumSales,
  getServerStatsCriteria,
  getServerStatsRevelOrderCounts,
  getServerStatsRevelAttribution,
} from './scopedReadsService';
import { calculateCriteriaCounts as sharedCalculateCriteriaCounts } from '../utils/criteriaUtils';
import { computeScore } from '../utils/scoreUtils';

interface ServerShift {
  shift_id: string;
  shift_created: string;
  shift_ended: string | null;
  duration_minutes: number;
  interactions_count: number;
  interactions?: Array<{
    id: string;
    data: any;
    start_time: number;
    end_time: number;
    shift_id: string;
    interaction_type: string;
  }>;
}

interface RevelMetricAttribution {
  upsellCount: number;
  purchaseCount: number;
  totalValue: number;
}

interface ServerStats {
  server_id: string;
  server_name: string;
  location_id: string;
  location_name: string;
  /** Server's default role (e.g. 'server', 'cashier'). Drives which
   *  RoleMetricConfig the scorecard uses for column labels. */
  role?: string | null;
  total_shifts: number;
  total_interactions: number;
  total_duration_minutes: number;
  initial_greeting_count: number;
  server_created: string;
  criteria_stats: {
    upsell_count: number;
    mention_count: number;
    describe_count: number;
    drink_promotion_count: number;
  };
  /**
   * Fulfilled-criteria totals keyed by every metric ``type`` the annotator
   * produced across this server's shifts. Lets downstream callers compute
   * percentages for role-specific keys (e.g. add_on_upsell) that don't have
   * dedicated ``criteria_stats.*`` fields.
   */
  metric_counts_by_key?: Record<string, number>;
  premium_stats: {
    regular_sales: number;
    premium_sales: number;
    premium_conversion_rate: number;
  };
  revel_attribution?: {
    metric_1: RevelMetricAttribution;
    metric_2: RevelMetricAttribution;
    metric_3: RevelMetricAttribution;
    drink_promotion: RevelMetricAttribution;
  };
  per_shift_avg_score?: number;
  shifts: ServerShift[];
}

interface GroupedServerStats extends ServerStats {
  date: string;
}

export class ServerLeaderboardService {
  // feat-133 Batch-6: the executeQuery (/admin/execute-select) method was removed —
  // it was the last raw-SQL seam here, fully dead once UpsellDashboard (#13) migrated
  // in Batch-5. getServerLeaderboardData now runs entirely on scopedReadsService.
  private static calculateCriteriaCounts(criteriaItems: any[]): {
    upsellCount: number;
    mentionCount: number;
    describeCount: number;
    drinkPromotionCount: number;
    initialGreetingCount: number;
  } {
    const result = sharedCalculateCriteriaCounts(criteriaItems);
    return {
      upsellCount: result.metricCounts['metric_1'] || 0,
      mentionCount: result.metricCounts['metric_2'] || 0,
      describeCount: result.metricCounts['metric_3'] || 0,
      drinkPromotionCount: result.drinkPromotionCount,
      initialGreetingCount: result.initialGreetingCount,
    };
  }

  public static async getServerLeaderboardData(
    startDate: string,
    endDate: string,
    serverIds?: string[],
    groupByDate: boolean = false,
    locationId?: string,
    skipTranscriptParts: boolean = false,
    tenantId?: string
  ): Promise<ServerStats[] | GroupedServerStats[]> {
    // In dev mode, return dummy data
    if (config.useDummyData) {
      console.log('[DEV MODE] Returning dummy server leaderboard data');
      return dummyServerStats as ServerStats[];
    }

    // feat-133 Batch-3: the aggregation now runs against tenant-scoped endpoints
    // (bound server-side from UserContext). A tenant is REQUIRED — there is no
    // all-tenants aggregation path (ADR 0003 DP-3a option-b): admins select a
    // tenant via TenantContext, and admin-without-tenant is blocked at the
    // consumer (ServerLeaderBoard / CombinedServerPerformanceAnalysis bail).
    if (!tenantId) {
      console.warn(
        'getServerLeaderboardData: no tenantId in scope — scoped aggregation requires a tenant; returning []',
      );
      return [];
    }

    try {
      // #1 shifts + #4 premium-sales. Premium-sales ignores location (matching the
      // original query, which scoped by tenant only).
      const [shifts, premiumSales] = await Promise.all([
        getServerStatsShifts(tenantId, { start: startDate, end: endDate, locationId, serverIds }),
        getServerStatsPremiumSales(tenantId, { start: startDate, end: endDate }),
      ]);

      console.log('ServerLeaderboardService: Raw shifts data:', shifts);
      console.log('ServerLeaderboardService: Raw premium sales data:', premiumSales);
      if (shifts.length > 0) {
        console.log('ServerLeaderboardService: First shift:', shifts[0]);
        console.log('ServerLeaderboardService: First shift server_id:', shifts[0].server_id);
        console.log('ServerLeaderboardService: First shift shift_id:', shifts[0].shift_id);
      }

      if (shifts.length === 0) {
        return [];
      }

      // Get all shift IDs (raw) to batch into the scoped endpoints. Foreign
      // shift_ids contribute ZERO rows server-side (the closed cross-tenant
      // aggregation leak); the FE batching of 30 is unchanged.
      const shiftIds = shifts.map((shift: any) => shift.shift_id);

      // #2 criteria — batched in groups of 30 shift IDs.
      const BATCH_SIZE = 30;
      const criteriaResults = [];
      for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
        const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
        const batchResults = await getServerStatsCriteria(tenantId, batchShiftIds);
        criteriaResults.push(...batchResults);
      }

      // #5 Revel order counts per shift (used as greetings when available; non-fatal).
      let revelOrderCountByShift: Record<string, number> = {};
      try {
        for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
          const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
          const batchResults = await getServerStatsRevelOrderCounts(tenantId, batchShiftIds);
          for (const row of batchResults) {
            revelOrderCountByShift[row.shift_id] = parseInt(row.order_count, 10) || 0;
          }
        }
        console.log('Revel order counts loaded for', Object.keys(revelOrderCountByShift).length, 'shifts');
      } catch (err) {
        console.warn('Revel order count fetch failed (tables may not exist yet):', err);
      }

      // Fetch Revel POS attribution data aggregated per server (non-fatal)
      // Key: server_id -> match_rule -> { purchaseCount, totalValue }
      let revelByServer: Record<string, Record<string, { purchaseCount: number; totalValue: number }>> = {};
      try {
        const revelResults = [];
        for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
          const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
          const batchResults = await getServerStatsRevelAttribution(tenantId, batchShiftIds);
          revelResults.push(...batchResults);
        }

        for (const row of revelResults) {
          const sid = row.server_id;
          if (!revelByServer[sid]) revelByServer[sid] = {};
          const existing = revelByServer[sid][row.match_rule];
          if (existing) {
            existing.purchaseCount += parseInt(row.purchase_count, 10) || 0;
            existing.totalValue += parseFloat(row.total_value) || 0;
          } else {
            revelByServer[sid][row.match_rule] = {
              purchaseCount: parseInt(row.purchase_count, 10) || 0,
              totalValue: parseFloat(row.total_value) || 0,
            };
          }
        }
        console.log('Revel attribution data loaded for', Object.keys(revelByServer).length, 'servers');
      } catch (revelErr) {
        console.warn('Revel attribution fetch failed (tables may not exist yet):', revelErr);
      }

      // Group shifts by server_id
      const shiftsByServer = shifts.reduce((acc: { [key: string]: any[] }, shift: any) => {
        if (!acc[shift.server_id]) {
          acc[shift.server_id] = [];
        }

        // Calculate shift duration
        let durationMinutes = 0;
        if (shift.shift_created && shift.shift_ended) {
          try {
            const startTime = new Date(shift.shift_created.endsWith('Z')
              ? shift.shift_created
              : `${shift.shift_created}Z`);

            const endTime = new Date(shift.shift_ended.endsWith('Z')
              ? shift.shift_ended
              : `${shift.shift_ended}Z`);

            // Check if dates are valid
            if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
              console.warn('Invalid date values:', { 
                shift_created: shift.shift_created, 
                shift_ended: shift.shift_ended 
              });
              durationMinutes = 0;
            } else {
              durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
            }
          } catch (error) {
            console.error('Error calculating shift duration:', error, {
              shift_created: shift.shift_created,
              shift_ended: shift.shift_ended
            });
            durationMinutes = 0;
          }
        }

        acc[shift.server_id].push({
          ...shift,
          duration_minutes: durationMinutes
        });

        return acc;
      }, {});

      // Group criteria by shift_id
      const criteriaByShift = criteriaResults.reduce((acc: { [key: string]: any[] }, item: any) => {
        if (!acc[item.shift_id]) {
          acc[item.shift_id] = [];
        }
        acc[item.shift_id].push(item);
        return acc;
      }, {});

      // Get server details for all servers with shifts
      console.log('ServerLeaderboardService: shiftsByServer:', shiftsByServer);
      console.log('ServerLeaderboardService: Object.keys(shiftsByServer):', Object.keys(shiftsByServer));
      // #3 servers — metadata for all servers that have shifts (raw id array).
      const serverIdsList = Object.keys(shiftsByServer);
      const servers = await getServerStatsServers(tenantId, { serverIds: serverIdsList, locationId });
      console.log('ServerLeaderboardService: Raw servers data:', servers);
      if (servers.length > 0) {
        console.log('ServerLeaderboardService: First server:', servers[0]);
        console.log('ServerLeaderboardService: First server name:', servers[0].server_name);
      }

      // Process all data to create aggregated server stats
      // `any[]` preserves the prior behavior: the aggregation rows were consumed
      // as `any` (executeQuery returned Promise<any>), so the ServerStats assembly
      // below — and the groupByDate flatten — stay byte-identical and untouched.
      const stats: any[] = servers.map((server: any) => {
        const serverShifts = shiftsByServer[server.server_id] || [];
        const serverPremiumSales = premiumSales.filter((s: any) => s.revel_server_id === server.revel_server_id);

        // Initialize counters
        let totalInteractions = 0;
        let totalDurationMinutes = 0;
        let initialGreetingCount = 0;
        let upsellCount = 0;
        let mentionCount = 0;
        let describeCount = 0;
        let drinkPromotionCount = 0;
        // Per-metric-key totals for dynamic role configs (e.g. Cashier's
        // add_on_upsell / dessert_upsell / …). Accumulates across all of
        // this server's shifts, only including shifts with ≥1 greeting.
        const metricCountsByKey: Record<string, number> = {};

        // Process each shift for this server, calculating per-shift scores
        const perShiftScores: number[] = [];
        const processedShifts = serverShifts.map((shift: any) => {
          totalInteractions += Number(shift.interactions_count) || 0;
          totalDurationMinutes += shift.duration_minutes || 0;

          // Get interactions for this shift
          const shiftCriteria = criteriaByShift[shift.shift_id] || [];
          const criteriaCounts = this.calculateCriteriaCounts(shiftCriteria);

          // Discover every metric type the annotator emitted in this shift's
          // interactions, then count fulfillments for all of them so
          // role-specific keys can flow through to the table.
          const discoveredKeys = new Set<string>();
          for (const item of shiftCriteria) {
            const arr = (item as any)?.interaction_data?.service_criteria;
            if (Array.isArray(arr)) {
              for (const c of arr) {
                if (c?.type) discoveredKeys.add(c.type);
              }
            }
          }
          const allMetricKeys = Array.from(discoveredKeys);
          const fullCounts = allMetricKeys.length > 0
            ? sharedCalculateCriteriaCounts(shiftCriteria, allMetricKeys)
            : null;

          // Only add criteria counts from shifts that have at least one greeting.
          // Shifts with no greetings show 0% in performance history and should not
          // inflate the aggregated scorecard totals.
          if (criteriaCounts.initialGreetingCount > 0) {
            upsellCount += criteriaCounts.upsellCount;
            mentionCount += criteriaCounts.mentionCount;
            describeCount += criteriaCounts.describeCount;
            drinkPromotionCount += criteriaCounts.drinkPromotionCount;
            if (fullCounts) {
              for (const key of allMetricKeys) {
                metricCountsByKey[key] =
                  (metricCountsByKey[key] || 0) + (fullCounts.metricCounts[key] || 0);
              }
            }
          }

          // Calculate per-shift Revel-adjusted greetings
          const revelCount = revelOrderCountByShift[shift.shift_id] || 0;
          const shiftBaseGreetings = revelCount > 0 ? revelCount : criteriaCounts.initialGreetingCount;
          const shiftMaxUpsells = Math.max(criteriaCounts.upsellCount, criteriaCounts.mentionCount, criteriaCounts.describeCount, criteriaCounts.drinkPromotionCount);
          const shiftGreetings = Math.max(shiftBaseGreetings, shiftMaxUpsells);
          initialGreetingCount += shiftGreetings;

          // Calculate per-shift percentages and score
          const shiftUpsellPct = shiftGreetings > 0 ? Math.round((criteriaCounts.upsellCount / shiftGreetings) * 100) : 0;
          const shiftMentionPct = shiftGreetings > 0 ? Math.round((criteriaCounts.mentionCount / shiftGreetings) * 100) : 0;
          const shiftDescribePct = shiftGreetings > 0 ? Math.round((criteriaCounts.describeCount / shiftGreetings) * 100) : 0;
          const shiftDrinkPct = shiftGreetings > 0 ? Math.round((criteriaCounts.drinkPromotionCount / shiftGreetings) * 100) : 0;
          const shiftScore = computeScore([shiftUpsellPct, shiftMentionPct, shiftDescribePct], shiftDrinkPct);
          perShiftScores.push(shiftScore);

          return {
            shift_id: shift.shift_id,
            shift_created: shift.shift_created,
            shift_ended: shift.shift_ended,
            duration_minutes: shift.duration_minutes,
            interactions_count: Number(shift.interactions_count) || 0,
            interactions: shiftCriteria.map((interaction: any) => ({
              id: interaction.interaction_id,
              data: interaction.interaction_data,
              start_time: interaction.start_time,
              end_time: interaction.end_time,
              shift_id: interaction.shift_id,
              interaction_type: interaction.interaction_type
            }))
          };
        });
        console.log('ServerLeaderboardService: *************** processedShifts =', processedShifts);

        // Average of per-shift scores
        const perShiftAvgScore = perShiftScores.length > 0
          ? Math.round(perShiftScores.reduce((sum, s) => sum + s, 0) / perShiftScores.length)
          : 0;

        // Build revel attribution for this server
        const serverRevel = revelByServer[server.server_id] || {};
        const emptyAttr: RevelMetricAttribution = { upsellCount: 0, purchaseCount: 0, totalValue: 0 };
        const buildAttr = (matchRule: string, upsells: number): RevelMetricAttribution => ({
          upsellCount: upsells,
          purchaseCount: serverRevel[matchRule]?.purchaseCount || 0,
          totalValue: serverRevel[matchRule]?.totalValue || 0,
        });

        const baseStats = {
          server_id: server.server_id,
          server_name: server.server_name,
          location_id: server.location_id,
          location_name: server.location_name,
          role: server.role ?? null,
          total_shifts: serverShifts.length,
          total_interactions: totalInteractions,
          total_duration_minutes: totalDurationMinutes,
          initial_greeting_count: initialGreetingCount,
          server_created: server.server_created,
          criteria_stats: {
            upsell_count: upsellCount,
            mention_count: mentionCount,
            describe_count: describeCount,
            drink_promotion_count: drinkPromotionCount
          },
          metric_counts_by_key: metricCountsByKey,
          premium_stats: {
            regular_sales: serverPremiumSales.reduce((sum: number, sale: any) => sum + sale.regular_sales, 0),
            premium_sales: serverPremiumSales.reduce((sum: number, sale: any) => sum + sale.premium_sales, 0),
            premium_conversion_rate: serverPremiumSales.length > 0
              ? serverPremiumSales.reduce((sum: number, sale: any) => sum + sale.premium_conversion_rate, 0) / serverPremiumSales.length
              : 0
          },
          revel_attribution: {
            metric_1: buildAttr('metric_1', upsellCount),
            metric_2: buildAttr('metric_2', mentionCount),
            metric_3: buildAttr('metric_3', describeCount),
            drink_promotion: buildAttr('drink_promotion', drinkPromotionCount),
          },
          per_shift_avg_score: perShiftAvgScore,
          shifts: processedShifts
        };

        if (groupByDate) {
          // Group shifts by date and create separate stats for each date
          const shiftsByDate = processedShifts.reduce((acc: { [key: string]: ServerShift[] }, shift: ServerShift) => {
            try {
              // Convert shift_created to PST for date grouping
              const dateString = shift.shift_created.endsWith('Z') ? shift.shift_created : shift.shift_created + "Z";
              const dateObj = new Date(dateString);
              
              if (isNaN(dateObj.getTime())) {
                console.warn('Invalid shift_created date:', shift.shift_created);
                return acc; // Skip this shift
              }
              
              const pstDate = toZonedTime(dateObj, 'America/Los_Angeles');
              const date = format(pstDate, 'yyyy-MM-dd');

              if (!acc[date]) {
                acc[date] = [];
              }
              acc[date].push(shift);
            } catch (error) {
              console.error('Error processing shift date:', error, { shift_created: shift.shift_created });
            }
            return acc;
          }, {});

          return (Object.entries(shiftsByDate) as [string, ServerShift[]][]).map(([date, dateShifts]) => {
            // Get premium sales for this date
            const datePremiumSales = serverPremiumSales.find((s: any) => s.sale_date === date) || {
              regular_sales: 0,
              premium_sales: 0,
              premium_conversion_rate: 0
            };

            // Recalculate stats for this date's shifts
            const dateInteractions = dateShifts.reduce((sum: number, shift: ServerShift) => sum + shift.interactions_count, 0);
            const dateDuration = dateShifts.reduce((sum: number, shift: ServerShift) => sum + shift.duration_minutes, 0);

            // Get interactions for this date
            const dateCriteria = dateShifts.flatMap((shift: ServerShift) => criteriaByShift[shift.shift_id] || []);
            const dateCriteriaCounts = this.calculateCriteriaCounts(dateCriteria);

            return {
              ...baseStats,
              date,
              total_shifts: dateShifts.length,
              total_interactions: dateInteractions,
              total_duration_minutes: dateDuration,
              initial_greeting_count: dateCriteriaCounts.initialGreetingCount,
              criteria_stats: {
                upsell_count: dateCriteriaCounts.upsellCount,
                mention_count: dateCriteriaCounts.mentionCount,
                describe_count: dateCriteriaCounts.describeCount,
                drink_promotion_count: dateCriteriaCounts.drinkPromotionCount
              },
              premium_stats: {
                regular_sales: datePremiumSales.regular_sales,
                premium_sales: datePremiumSales.premium_sales,
                premium_conversion_rate: datePremiumSales.premium_conversion_rate
              },
              shifts: dateShifts
            };
          });
        }

        return baseStats;
      });

      console.log('ServerLeaderboardService: Final stats before return:', stats);
      if (stats.length > 0) {
        console.log('ServerLeaderboardService: First final stat:', stats[0]);
        console.log('ServerLeaderboardService: First final stat server_name:', stats[0].server_name);
      }

      return groupByDate ? stats.flat() : stats;
    } catch (error) {
      console.error('Error fetching server leaderboard data:', error);
      Sentry.captureException(error);
      throw error;
    }
  }
} 